/**
 * SearchAiz Search Client
 *
 * Wrapper around the VectorAiz service API for search operations.
 * Provides:
 * - Unified search across multiple sources
 * - AI reranking integration
 * - AI analysis integration
 * - Error handling and retry logic
 * - Response transformation to SearchAiz types
 */

import type {
  SearchRequest,
  SearchResponse,
  SearchResult,
  SearchPattern,
  RerankConfig,
  AnalysisConfig,
  SearchFilters,
  AiAnalysis,
  FacetCount,
  DocumentResult,
  RepositoryResult,
} from '../types';
import { getCollectionsForSources, getBestPatternForSources } from '../utils/dataSourceRegistry';
import {
  getSourceDocumentTitle,
  getSourceFileName,
  getSourceSecondaryFileName,
} from '@/lib/sourceCitationTitles';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * API proxy URL - routes through Next.js API to VectorAiz
 * This avoids CORS issues and allows server-side auth
 */
const VECTORAIZ_PROXY_URL = '/api/knowledge-base';

/**
 * Default timeout for search requests (ms)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Max retries for failed requests
 */
const MAX_RETRIES = 2;

// =============================================================================
// API CLIENT
// =============================================================================

interface ApiRequestOptions extends RequestInit {
  timeout?: number;
}

type SearchRequestWithSignal = SearchRequest & { signal?: AbortSignal };

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeout);

  const abortFromExternal = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('Aborted', 'AbortError');
    }
    signal.addEventListener('abort', abortFromExternal, { once: true });
  }

  try {
    return await fetch(input, {
      ...fetchOptions,
      signal: controller.signal,
    });
  } catch (error) {
    if (timedOut && isAbortError(error)) {
      throw new SearchApiError('Request timed out', 408, 'Timeout');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromExternal);
  }
}

/**
 * Make an API request with timeout and error handling
 */
async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;

  try {
    const url = `${VECTORAIZ_PROXY_URL}${endpoint}`;

    const response = await fetchWithTimeout(url, {
      ...fetchOptions,
      timeout,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new SearchApiError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
        errorText,
      );
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    return JSON.parse(text) as T;
  } catch (error) {
    if (error instanceof SearchApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      throw new SearchApiError(error.message, 0, error.message);
    }

    throw new SearchApiError('Unknown error', 0, String(error));
  }
}

/**
 * Custom error class for search API errors
 */
export class SearchApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details: string,
  ) {
    super(message);
    this.name = 'SearchApiError';
  }
}

// =============================================================================
// SEARCH FUNCTIONS
// =============================================================================

/**
 * VectorAiz API request format
 */
interface VectorAizSearchRequest {
  query: string;
  collections?: string[];
  k?: number;
  rerank?: boolean;
  rerank_tier?: 'fast' | 'standard' | 'quality';
  rerank_threshold?: number;
  ai_analysis?: boolean;
  ai_analysis_mode?: 'structured' | 'freeform';
  filters?: Record<string, unknown>;
  include_metadata?: boolean;
}

/**
 * VectorAiz API response format
 */
interface VectorAizSearchResponse {
  results: VectorAizResult[];
  total_count: number;
  duration_ms: number;
  ai_analysis?: {
    summary: string;
    insights: string[];
    suggested_queries: string[];
    relevance_explanation?: string;
    structured_data?: Record<string, unknown>;
  };
  facets?: Record<string, Array<{ value: string; count: number }>>;
}

interface VectorAizResult {
  id: string;
  collection: string;
  type: string;
  score: number;
  rerank_score?: number;
  payload: Record<string, unknown>;
  highlights?: Array<{ start: number; end: number; text: string }>;
}

/**
 * Transform VectorAiz result to SearchAiz result
 */
function transformResult(vResult: VectorAizResult): SearchResult {
  const { payload } = vResult;

  // Determine result type from collection and payload
  const resultType = determineResultType(vResult.collection, payload);
  const titleFields = {
    documentTitle: payload.documentTitle ?? payload.document_title,
    document_title: payload.document_title,
    title: payload.title ?? payload.name,
    fileName: payload.fileName ?? payload.filename ?? payload.file_name,
    filename: payload.filename ?? payload.fileName ?? payload.file_name,
  };
  const fileName = getSourceFileName(titleFields);
  const secondaryFileName = getSourceSecondaryFileName(titleFields);

  // Build base result
  const result: SearchResult = {
    id: vResult.id,
    source: mapCollectionToSource(vResult.collection),
    type: resultType,
    title: getSourceDocumentTitle(titleFields, 'Untitled'),
    subtitle: (payload.subtitle as string | undefined) || secondaryFileName,
    preview:
      (payload.content as string) || (payload.description as string) || (payload.preview as string),
    score: vResult.score,
    rerankScore: vResult.rerank_score,
    url: payload.url as string | undefined,
    metadata: {
      ...payload,
      ...(fileName ? { fileName } : {}),
    },
    highlights: vResult.highlights,
    timestamp: (payload.created_at as string) || (payload.updated_at as string),
  };

  return result;
}

/**
 * Determine result type from collection and payload
 */
function determineResultType(
  collection: string,
  payload: Record<string, unknown>,
): SearchResult['type'] {
  // Map collections to types
  const collectionTypeMap: Record<string, SearchResult['type']> = {
    repositories: 'repository',
    repository_chunks: 'document',
    product_catalog: 'product',
    product_specs: 'product',
    product_files: 'product',
    customers: 'customer',
    contacts: 'contact',
    invoices: 'customer',
    orders_history: 'order',
    tickets_history: 'ticket',
    tenant_lessons: 'support',
    calls: 'call',
    entities: 'entity',
    web: 'web',
    laws_policies: 'operations',
    service_procedures: 'operations',
    training_materials: 'operations',
    incident_patterns: 'operations',
    realtime_chunks: 'realtime',
    suggested_actions: 'realtime',
    call_attachments: 'realtime',
    pages: 'page',
    settings: 'setting',
    api_docs: 'page',
    help_docs: 'page',
    llm_metrics: 'metric',
    user_activity: 'metric',
    billing_invoices: 'metric',
    system_events: 'metric',
  };

  return collectionTypeMap[collection] || 'document';
}

/**
 * Map collection to data source ID
 */
function mapCollectionToSource(collection: string): string {
  const collectionSourceMap: Record<string, string> = {
    repositories: 'knowledge_base',
    repository_chunks: 'knowledge_base',
    product_catalog: 'product_data',
    product_specs: 'product_data',
    product_files: 'product_data',
    customers: 'business_data',
    contacts: 'business_data',
    invoices: 'business_data',
    orders_history: 'support_data',
    tickets_history: 'support_data',
    tenant_lessons: 'support_data',
    calls: 'calls',
    entities: 'entities',
    web: 'web',
    laws_policies: 'operations',
    service_procedures: 'operations',
    training_materials: 'operations',
    incident_patterns: 'operations',
    realtime_chunks: 'realtime',
    suggested_actions: 'realtime',
    call_attachments: 'realtime',
    pages: 'application',
    settings: 'application',
    api_docs: 'application',
    help_docs: 'application',
    llm_metrics: 'analytics',
    user_activity: 'analytics',
    billing_invoices: 'analytics',
    system_events: 'analytics',
  };

  return collectionSourceMap[collection] || 'knowledge_base';
}

/**
 * Transform VectorAiz AI analysis to SearchAiz format
 */
function transformAnalysis(
  vAnalysis: VectorAizSearchResponse['ai_analysis'],
): AiAnalysis | undefined {
  if (!vAnalysis) return undefined;

  return {
    summary: vAnalysis.summary,
    insights: vAnalysis.insights,
    suggestedQueries: vAnalysis.suggested_queries,
    relevanceExplanation: vAnalysis.relevance_explanation,
    structuredData: vAnalysis.structured_data,
  };
}

/**
 * Transform VectorAiz facets to SearchAiz format
 */
function transformFacets(vFacets: VectorAizSearchResponse['facets']): Record<string, FacetCount[]> {
  if (!vFacets) return {};

  const facets: Record<string, FacetCount[]> = {};

  for (const [key, values] of Object.entries(vFacets)) {
    if (Array.isArray(values)) {
      facets[key] = values.map((v) => ({
        value: v.value,
        count: v.count,
      }));
    }
  }

  return facets;
}

// =============================================================================
// PLATFORM SEARCH (Supabase-backed sources: calls, entities)
// =============================================================================

/** Sources that query Supabase/custom endpoints instead of VectorAiz */
const PLATFORM_SOURCES = new Set(['calls', 'entities', 'web']);

/**
 * Search platform data via Supabase-backed API route + web search
 */
async function searchPlatform(
  query: string,
  sources: string[],
  limit: number,
  signal?: AbortSignal,
): Promise<SearchResult[]> {
  const fetches: Promise<SearchResult[]>[] = [];

  // Supabase-backed sources (calls, entities)
  const dbSources = sources.filter((s) => s !== 'web');
  if (dbSources.length > 0) {
    const params = new URLSearchParams({
      q: query,
      sources: dbSources.join(','),
      limit: String(limit),
    });
    fetches.push(
      fetchWithTimeout(`/api/search/platform?${params}`, {
        headers: { 'Content-Type': 'application/json' },
        signal,
      })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((d) => (Array.isArray(d.results) ? d.results : []))
        .catch((error) => {
          if (isAbortError(error)) throw error;
          return [];
        }),
    );
  }

  // Web search (OpenAI-backed)
  if (sources.includes('web')) {
    fetches.push(
      fetchWithTimeout('/api/search/web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
        signal,
      })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((d) =>
          (Array.isArray(d.results) ? d.results : []).map(
            (w: Record<string, unknown>, i: number): SearchResult => ({
              id: `web-${i}-${String(w.url || '').slice(0, 40)}`,
              source: 'web',
              type: 'web' as const,
              title: String(w.title || ''),
              preview: String(w.snippet || ''),
              score: 0.7,
              url: String(w.url || ''),
              metadata: { domain: w.domain },
            }),
          ),
        )
        .catch((error) => {
          if (isAbortError(error)) throw error;
          return [];
        }),
    );
  }

  const allResults = await Promise.all(fetches);
  return sortSearchResults(allResults.flat());
}

function applyResultLimit(results: SearchResult[], limit: number): SearchResult[] {
  if (!Number.isFinite(limit) || limit <= 0) return results;
  return results.slice(0, limit);
}

function getResultRankScore(result: SearchResult): number {
  const score = result.rerankScore ?? result.score;
  return Number.isFinite(score) ? score : 0;
}

function sortSearchResults(results: SearchResult[]): SearchResult[] {
  return results
    .map((result, index) => ({ result, index }))
    .sort((a, b) => {
      const scoreDiff = getResultRankScore(b.result) - getResultRankScore(a.result);
      if (scoreDiff !== 0) return scoreDiff;

      const sourceDiff = Number(a.result.source === 'web') - Number(b.result.source === 'web');
      if (sourceDiff !== 0) return sourceDiff;

      return a.index - b.index;
    })
    .map(({ result }) => result);
}

// =============================================================================
// AI QUERY EXPANSION
// =============================================================================

/**
 * Expand a search query into multiple related queries using AI
 */
async function expandQuery(query: string, signal?: AbortSignal): Promise<string[]> {
  try {
    const res = await fetchWithTimeout('/api/search/expand', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
      signal,
    });
    if (!res.ok) return [query];
    const data = await res.json();
    return Array.isArray(data.queries) && data.queries.length > 0 ? data.queries : [query];
  } catch (error) {
    if (isAbortError(error)) throw error;
    return [query];
  }
}

// =============================================================================
// MAIN SEARCH FUNCTION
// =============================================================================

/**
 * Perform a search across multiple sources
 * Hybrid: platform sources (calls, entities, web) query Supabase/custom;
 * all other sources query VectorAiz/Qdrant.
 * Optional AI query expansion runs additional queries in parallel.
 */
export async function search(request: SearchRequestWithSignal): Promise<SearchResponse> {
  const startTime = performance.now();

  // Step 1: Optional AI query expansion
  let expandedQueries: string[] | undefined;
  let queries = [request.query];

  if (request.expand) {
    expandedQueries = await expandQuery(request.query, request.signal);
    queries = expandedQueries;
  }

  // Step 2: Run searches for all queries in parallel
  const allSearches = queries.map((q) => searchSingleQuery({ ...request, query: q }));
  const allResponses = await Promise.all(allSearches);

  // Step 3: Merge and deduplicate results
  const seenIds = new Set<string>();
  const mergedResults: SearchResult[] = [];
  let totalCount = 0;

  for (const response of allResponses) {
    for (const result of response.results) {
      if (!seenIds.has(result.id)) {
        seenIds.add(result.id);
        mergedResults.push(result);
      }
    }
    totalCount += response.totalCount;
  }

  const limitedResults = applyResultLimit(
    sortSearchResults(mergedResults),
    request.limit * (request.expand ? 2 : 1),
  );

  return {
    results: limitedResults,
    totalCount,
    durationMs: Math.round(performance.now() - startTime),
    analysis: allResponses[0]?.analysis,
    suggestions: allResponses[0]?.suggestions,
    facets: allResponses[0]?.facets,
    expandedQueries,
  };
}

/**
 * Execute a single search query across all sources (internal helper)
 */
async function searchSingleQuery(request: SearchRequestWithSignal): Promise<SearchResponse> {
  const startTime = performance.now();

  // Split sources into platform (Supabase/web) vs vector (VectorAiz)
  const platformSources = request.sources.filter((s) => PLATFORM_SOURCES.has(s));
  const vectorSources = request.sources.filter((s) => !PLATFORM_SOURCES.has(s));

  // Fire platform search in parallel with VectorAiz search
  const platformPromise =
    platformSources.length > 0
      ? searchPlatform(request.query, platformSources, request.limit, request.signal)
      : Promise.resolve([]);

  // If no vector sources, just return platform results
  if (vectorSources.length === 0) {
    const platformResults = await platformPromise;
    const limitedResults = applyResultLimit(sortSearchResults(platformResults), request.limit);
    return {
      results: limitedResults,
      totalCount: platformResults.length,
      durationMs: Math.round(performance.now() - startTime),
    };
  }

  // Get collections for vector sources only
  const collections = getCollectionsForSources(vectorSources);

  // Build VectorAiz request
  const vRequest: VectorAizSearchRequest = {
    query: request.query,
    collections,
    k: request.limit,
    include_metadata: true,
  };

  // Add rerank config
  if (request.rerank?.enabled) {
    vRequest.rerank = true;
    vRequest.rerank_tier = request.rerank.tier;
    vRequest.rerank_threshold = request.rerank.scoreThreshold;
  }

  // Add analysis config
  if (request.analysis?.enabled) {
    vRequest.ai_analysis = true;
    vRequest.ai_analysis_mode = request.analysis.mode;
  }

  // Add filters
  if (request.filters) {
    vRequest.filters = transformFiltersToApi(request.filters);
  }

  // Determine endpoint based on pattern
  const endpoint = getEndpointForPattern(request.pattern);

  // Build the correct request body for the endpoint
  // The unified/repositories/documents/cascade endpoints use a different schema
  // than the old collection-based search endpoints
  const isRepoPattern =
    request.pattern === 'unified' ||
    request.pattern === 'repositories' ||
    request.pattern === 'cascade';
  let requestBody: Record<string, unknown>;
  if (isRepoPattern) {
    requestBody = {
      query: request.query,
      // Send 'all' so backend RLS (filterRepositoryIdsByAccess) gates results
      // to repos the user can SELECT. Hardcoded 'public' double-gates and
      // hides org/private repos the user owns. See #317.
      visibilityFilter: 'all',
      repositoryK: Math.min(request.limit || 10, 50),
      documentK: Math.min(request.limit || 25, 100),
    };
  } else {
    requestBody = vRequest as unknown as Record<string, unknown>;
  }

  // Make request with retry
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = await apiRequest<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        signal: request.signal,
      });

      let response: SearchResponse;

      if (isRepoPattern && raw.data) {
        // Unified/repository endpoints return { data: { repositories, documents }, counts, totalTimeMs }
        const repos = Array.isArray(raw.data.repositories) ? raw.data.repositories : [];
        const docs = Array.isArray(raw.data.documents) ? raw.data.documents : [];
        const merged: SearchResult[] = [
          ...repos.map((r: Record<string, unknown>) => ({
            id: (r.supabaseId || r.id) as string,
            type: 'repository' as const,
            title: (r.name as string) || '',
            preview: (r.text as string) || undefined,
            score: (r.score as number) || 0,
            source: 'knowledge_base',
            metadata: { repositoryId: r.supabaseId, slug: r.slug, visibility: r.visibility },
          })),
          ...docs.map((d: Record<string, unknown>) => {
            const titleFields = {
              documentTitle: d.documentTitle ?? d.document_title,
              document_title: d.document_title,
              title: d.title ?? d.name,
              fileName: d.fileName ?? d.filename ?? d.file_name,
              filename: d.filename ?? d.fileName ?? d.file_name,
            };
            const fileName = getSourceFileName(titleFields);
            return {
              id: (d.chunkId || d.documentId || d.id) as string,
              type: 'document' as const,
              title: getSourceDocumentTitle(
                titleFields,
                String(d.sectionPath || d.documentId || 'Untitled Document'),
              ),
              subtitle: getSourceSecondaryFileName(titleFields),
              preview: (d.textPreview || d.text || undefined) as string | undefined,
              score: (d.score as number) || 0,
              source: 'knowledge_base',
              metadata: {
                repositoryId: d.repositoryId,
                documentId: d.documentId,
                chunkId: d.chunkId,
                ...(fileName ? { fileName } : {}),
              },
            };
          }),
        ].sort((a, b) => b.score - a.score);

        response = {
          results: merged,
          totalCount: (raw.counts?.repositories ?? 0) + (raw.counts?.documents ?? 0),
          durationMs: raw.totalTimeMs || Math.round(performance.now() - startTime),
          analysis: undefined,
          suggestions: undefined,
          facets: {},
        };
      } else {
        // Collection-based endpoints return { results, total_count, duration_ms }
        const vResponse = raw as VectorAizSearchResponse;
        const rawResults = Array.isArray(vResponse.results) ? vResponse.results : [];
        response = {
          results: rawResults.map(transformResult),
          totalCount: vResponse.total_count ?? 0,
          durationMs: vResponse.duration_ms || Math.round(performance.now() - startTime),
          analysis: transformAnalysis(vResponse.ai_analysis),
          suggestions: vResponse.ai_analysis?.suggested_queries,
          facets: transformFacets(vResponse.facets),
        };
      }

      // Merge platform results (if any) with VectorAiz results
      const platformResults = await platformPromise;
      if (platformResults.length > 0) {
        const mergedResults = sortSearchResults([...platformResults, ...response.results]);
        response.results = applyResultLimit(mergedResults, request.limit);
        response.totalCount += platformResults.length;
      } else {
        response.results = applyResultLimit(sortSearchResults(response.results), request.limit);
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      if (isAbortError(error)) {
        throw error;
      }

      // Don't retry on client errors (4xx)
      if (error instanceof SearchApiError && error.statusCode >= 400 && error.statusCode < 500) {
        // Still return platform results even if VectorAiz fails
        const platformResults = await platformPromise;
        if (platformResults.length > 0) {
          const limitedResults = applyResultLimit(
            sortSearchResults(platformResults),
            request.limit,
          );
          return {
            results: limitedResults,
            totalCount: platformResults.length,
            durationMs: Math.round(performance.now() - startTime),
          };
        }
        throw error;
      }

      // Wait before retry (exponential backoff)
      if (attempt < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 500));
      }
    }
  }

  // VectorAiz exhausted retries — still return platform results if available
  const platformResults = await platformPromise;
  if (platformResults.length > 0) {
    const limitedResults = applyResultLimit(sortSearchResults(platformResults), request.limit);
    return {
      results: limitedResults,
      totalCount: platformResults.length,
      durationMs: Math.round(performance.now() - startTime),
    };
  }

  throw lastError || new SearchApiError('Search failed after retries', 500, 'Unknown');
}

/**
 * Get the API endpoint for a search pattern
 */
function getEndpointForPattern(pattern: SearchPattern): string {
  const endpointMap: Record<SearchPattern, string> = {
    unified: '/search/unified',
    documents: '/search/documents',
    repositories: '/search/repositories',
    cascade: '/search/cascade',
    aggregate: '/search/aggregate',
  };

  return endpointMap[pattern] || '/search/unified';
}

/**
 * Transform SearchAiz filters to VectorAiz API format
 */
function transformFiltersToApi(filters: SearchFilters): Record<string, unknown> {
  const apiFilters: Record<string, unknown> = {};

  if (filters.dateRange) {
    if (filters.dateRange.start) {
      apiFilters.date_from = filters.dateRange.start;
    }
    if (filters.dateRange.end) {
      apiFilters.date_to = filters.dateRange.end;
    }
  }

  if (filters.types && filters.types.length > 0) {
    apiFilters.types = filters.types;
  }

  // Pass through custom field filters
  if (filters.fields) {
    Object.assign(apiFilters, filters.fields);
  }

  return apiFilters;
}

// =============================================================================
// SPECIALIZED SEARCH FUNCTIONS
// =============================================================================

/**
 * Quick search for header/command palette (optimized for speed)
 */
export async function quickSearch(
  query: string,
  sources: string[] = ['calls', 'knowledge_base', 'application'],
  limit: number = 5,
): Promise<SearchResponse> {
  return search({
    query,
    sources,
    pattern: 'unified',
    limit,
    // Disable AI features for speed
    rerank: undefined,
    analysis: undefined,
  });
}

/**
 * Full search with AI features for knowledge base
 */
export async function fullSearch(
  query: string,
  sources: string[],
  options: {
    pattern?: SearchPattern;
    limit?: number;
    rerank?: RerankConfig;
    analysis?: AnalysisConfig;
    filters?: SearchFilters;
  } = {},
): Promise<SearchResponse> {
  const pattern = options.pattern || getBestPatternForSources(sources);

  return search({
    query,
    sources,
    pattern,
    limit: options.limit || 20,
    rerank: options.rerank,
    analysis: options.analysis,
    filters: options.filters,
    expand: true, // AI query expansion for full search
  });
}

/**
 * Conversational search (with context from previous turns)
 */
export async function conversationalSearch(
  query: string,
  sources: string[],
  conversationId?: string,
  previousContext?: string,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  // For now, just append context to query
  // In future, this could use a dedicated conversation endpoint
  const enrichedQuery = previousContext ? `Context: ${previousContext}\n\nQuery: ${query}` : query;

  const response = await search({
    query: enrichedQuery,
    sources,
    pattern: 'unified',
    limit: 10,
    rerank: { enabled: true, tier: 'standard', scoreThreshold: 0.3 },
    analysis: { enabled: true, mode: 'structured' },
    conversationId,
    signal,
  });

  // Generate a conversation ID if not provided
  if (!conversationId) {
    response.conversationId = `conv_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  return response;
}

// =============================================================================
// EXPORT
// =============================================================================

export const searchClient = {
  search,
  quickSearch,
  fullSearch,
  conversationalSearch,
};

export default searchClient;
